import { type CSSProperties, useEffect, useState } from 'react'
import { Modal, Form, Input, Alert, type FormInstance } from 'antd'
import './styles.scss'
import { ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons'
import { telMask } from 'utils/masks'
import IconClose from 'components/Icons/Close'
import Button from 'components/UI/Buttom'
import Check from 'components/Icons/Check'
import { Loading } from 'components/UI'
import { type Recommendation } from 'types/services/Recommedation'
import { useAccessibility } from 'context/hooks/useAccessibility'

import iconChecked from 'assets/icons/check-preenchido.svg'

interface ModalRecommendationProps {
  question: string
  answer: string
  justification: string
  modalIsOpen: boolean
  isAlert: boolean
  loading: boolean
  closeModal: () => void
  action: (form: FormInstance) => void
  initialValues: Recommendation
  editMode?: boolean
}

function ModalRecommendation({
  question,
  answer,
  justification,
  modalIsOpen,
  closeModal,
  action,
  isAlert,
  loading,
  initialValues,
  editMode = false,
}: ModalRecommendationProps) {
  const [form] = Form.useForm()

  const [fildsValidate, setFildsValidate] = useState(false)

  useEffect(() => {
    return () => {
      form.resetFields()
    }
  }, [modalIsOpen])

  const validateMessages = {
    // eslint-disable-next-line no-template-curly-in-string
    required: "O campo '${label}' é obrigatório!",
    types: {
      email: 'Este e-mail não é válido!',
    },
  }

  useEffect(() => {
    if (!editMode) return
    return () => {
      handleValidation()
    }
  }, [initialValues])

  const alert = isAlert ? 'alert' : ''
  const styleSituationIcon: CSSProperties = isAlert
    ? { fontSize: '32px', color: '#F67D7D' }
    : { fontSize: '32px', color: '#80C470' }

  function handleValidation() {
    form
      .validateFields([
        'institutionRecommender',
        'emailRecommender',
        'phoneRecommender',
        'recommendationText',
        'responsibleAgent',
      ])
      .then(() => {
        setFildsValidate(true)
      })
      .catch(() => {
        setFildsValidate(false)
      })
  }

  const { contrastIsSelected } = useAccessibility()

  if (loading) {
    return (
      <Modal
        className={`containerModalSend ${
          contrastIsSelected ? 'contrastMode' : ''
        }`}
        visible={modalIsOpen}
        bodyStyle={{
          backgroundColor: contrastIsSelected
            ? 'var(--contrast-background-color)'
            : 'var(--background)',
          ...(contrastIsSelected && {
            color: contrastIsSelected
              ? 'var(--contrast-foreground-color)'
              : '#2D4040',
            border: '1px solid var(--contrast-foreground-color)',
          }),
          borderRadius: '10px',
          padding: '0',
        }}
        closable={false}
        maskStyle={
          !contrastIsSelected
            ? { backgroundColor: 'rgba(45, 64, 64, 0.62)' }
            : { backgroundColor: 'var(--contrast-background-color)' }
        }
      >
        <Loading
          styles={{ padding: '30% 0' }}
          title={`Salvando a ${editMode ? 'edição na ' : ''}recomendação...`}
        />
      </Modal>
    )
  }

  return (
    <Modal
      forceRender
      className={`containerModal ${contrastIsSelected ? 'contrastMode' : ''}`}
      visible={modalIsOpen}
      onCancel={closeModal}
      closable={true}
      bodyStyle={{
        backgroundColor: contrastIsSelected
          ? 'var(--contrast-background-color)'
          : 'var(--background)',
        ...(contrastIsSelected && {
          color: contrastIsSelected
            ? 'var(--contrast-foreground-color)'
            : '#2D4040',
        }),
        borderRadius: '2px',
        padding: '0',
      }}
      closeIcon={
        <IconClose
          size="14px"
          color={
            contrastIsSelected
              ? 'var(--contrast-graphical-background-color)'
              : '#2D4040'
          }
        />
      }
      maskStyle={
        !contrastIsSelected
          ? { backgroundColor: 'rgba(45, 64, 64, 0.62)' }
          : { backgroundColor: 'var(--contrast-background-color)' }
      }
    >
      <div className="containerModal__main">
        <div className="containerModal__main-title">
          <h3> {!editMode ? 'Criar recomendação' : 'Editar recomendação'}</h3>
        </div>
        <div className={`containerModal__main-situation ${alert}`}>
          <div className="containerModal__main-situation__header">
            <div>
              <strong>{question}</strong>
              <span>{answer}</span>
            </div>
            {isAlert ? (
              <ExclamationCircleFilled style={styleSituationIcon} />
            ) : (
              <img src={iconChecked} alt="ok" height="32px" />
            )}
          </div>
          {isAlert && (
            <Alert
              description={justification}
              type="info"
              icon={<InfoCircleFilled />}
              showIcon
            />
          )}
        </div>
        <div className="containerModal__main-form">
          {!editMode && (
            <strong>
              Informe a instituição que deve ser notificada sobre a situação da
              família. Também é possível inserir dados específicos de um agente,
              caso prefira.
            </strong>
          )}
          <Form
            form={form}
            id="agent-form"
            layout="vertical"
            validateMessages={validateMessages}
            style={
              !editMode
                ? {
                    marginTop: '2rem',
                  }
                : {}
            }
            initialValues={initialValues}
          >
            <Form.Item
              name={'institutionRecommender'}
              label="Instituição"
              rules={[{ required: true }]}
            >
              <Input
                placeholder="Digite o nome da instituição"
                onChange={() => {
                  handleValidation()
                }}
              />
            </Form.Item>
            <Form.Item
              label="Nome do agente"
              name={'responsibleAgent'}
              className="optional"
            >
              <Input placeholder="Digite o nome do agente" />
            </Form.Item>
            <Form.Item
              name={'emailRecommender'}
              label="E-mail da instituição ou do agente"
              rules={[{ type: 'email', required: true }]}
            >
              <Input
                type="email"
                onChange={() => {
                  handleValidation()
                }}
                placeholder="Digite o e-mail da instituição ou do agente"
              />
            </Form.Item>
            <Form.Item
              label="Telefone da instituição ou do agente"
              name="phoneRecommender"
              rules={[
                {
                  required: true,
                },
                {
                  message: 'Formato inválido',
                  validator: async (_, value) => {
                    if (/(\(?\d{2}\)?\s)?(\d{4,5}-\d{4})/.test(value)) {
                      await Promise.resolve()
                    } else {
                      await Promise.reject(new Error('Formato inválido'))
                    }
                  },
                },
              ]}
            >
              <Input
                type="tel"
                placeholder="Digite o telefone da instituição ou do agente"
                maxLength={15}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setFieldsValue({
                    phoneRecommender: telMask(event.target.value),
                  })
                  handleValidation()
                }}
              />
            </Form.Item>
            <hr style={editMode ? { marginTop: '3rem' } : {}} />
            {!editMode && (
              <strong style={{ margin: '2rem 0' }}>
                Informe qual ação deve ser tomada pela instituição ou pelo
                agente mencionado acima.
              </strong>
            )}
            <Form.Item
              name={'recommendationText'}
              label="Descreva sua recomendação"
              rules={[{ required: true }]}
            >
              <Input.TextArea
                onChange={() => {
                  handleValidation()
                }}
                style={{ height: '70px' }}
                placeholder="Descreva sua recomendação aqui"
              />
            </Form.Item>
          </Form>
        </div>
        <div className="containerModal__main-buttons">
          {!editMode ? (
            <span>
              Ao salvar, você será adicionado como autor dessa nova
              recomendação.
            </span>
          ) : (
            <span>
              Ao salvar, será adicionado a data de edição na recomendação.
            </span>
          )}
          <Button
            title="Salvar"
            disabled={!fildsValidate}
            type="primary"
            icon={<Check />}
            onClick={() => {
              action(form)
            }}
          />
          <Button
            title="Cancelar"
            icon={<IconClose size="13px" />}
            onClick={() => {
              closeModal()
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ModalRecommendation
